import moment from 'moment';
import { evaluate } from 'mathjs';
import numeral from 'numeral';
import pixUtils from '@/services/common/pix.utils.js';
import pixEvaluateUtils from '@/services/common/pixEvaluate.utils';
import comparableUtils from './comparable.utils.js';
import pixNumeral from '@/services/common/pixNumeral.utils.js';
import computedOperationUtils from '@/services/common/computedOperation.utils';
import regexConst from '@/consts/regex.const.js';
import comparableTableFeaturesUtils from './comparableTableFeatures.utils.js';
import store from '@/store/index.js';

export default {
  //receives a comparable and a configuration and generates an object to represent a comparable in the popup or in the list
  generateCommonObject(comparable, config, isItem, { currentLang, userDateFormat, fallbackText }) {
    let value,
      paramLblFilter,
      paramLblFilterShortname,
      title,
      comparableClass,
      filterValue,
      prefix,
      suffix,
      label,
      columnWidth = '';

    const typologyField = 'PropertyTypeCode';
    const operationField = 'OperationTypeID';

    const matchesTypologyRestriction = !config.isOnlyForTypology || evaluate(config.isOnlyForTypology.replaceAll('this', pixUtils.getFieldValue(typologyField, comparable, null, null, currentLang)));
    const matchesOperationRestriction = !config.isOnlyForOperation || evaluate(config.isOnlyForOperation.replaceAll('this', pixUtils.getFieldValue(operationField, comparable, null, null, currentLang)));
    const matchesConditions = !config.Conditions || pixEvaluateUtils.evalConditions(config.Conditions, comparable, currentLang);

    if (matchesTypologyRestriction && matchesOperationRestriction && matchesConditions) {
      value = pixUtils.getFieldValue(config.MetricKey, comparable, config.DefaultValue, config.isFixed ? config.infixText : null, currentLang);

      if (config.ReadTransform) {
        try {
          value = computedOperationUtils.getValueFromComputedOperation(comparable, config.MetricKey, currentLang, config.ReadTransform);

          if (Number.isNaN(value) || (typeof value !== 'string' && !Number.isFinite(value)) || value === undefined) {
            value = config.DefaultValue || null;
          }
        } catch (error) {
          console.log(error);
        }
      }

      if (config.NumeralFormat !== null && config.NumeralFormat !== undefined && value && value !== config.DefaultValue) {
        const hasTwelveMonthFlag = comparableUtils.incomeExpenses12Month(comparable, currentLang);
        const measure = comparableUtils.getFieldValueInMeasures(comparable, config.MetricKey, hasTwelveMonthFlag);
        const result = this.getNumeralFormat(comparable, value, config.NumeralFormat, paramLblFilterShortname, measure);
        value = result.replacedValue;
        filterValue = result.filterValue;
        paramLblFilterShortname = result.paramLblFilterShortname;
      } else if (config.DateFormat) {
        value = value?.split('T')[0]; // if it comes with timezone strip it off
        value = value && value !== fallbackText ? moment(value).format(userDateFormat ?? config.DateFormat) : fallbackText;
      } else if (config.isBoolean) {
        value = value === true ? 'Yes' : value === true ? 'No' : value;
      }

      if (config.ParamListKey) {
        paramLblFilter = config.ParamListKey;
      } else if (config.ParamListKeyShortname) {
        paramLblFilterShortname = config.ParamListKeyShortname;
      }

        if (config.EmitEventName) {
            const customFn = popUpCustmonEventFn[config.EmitEventName] ?? fallbackText;
            if (customFn) value = customFn(comparable, { userDateFormat, NumeralFormat: config.NumeralFormat });
      }

      prefix = config.PrefixText;
      suffix = config.SuffixText;
      if (config.isInTheSameSlot) {
        return {
          value: `${value ?? ''}`,
          inSameRow: true,
          paramLblFilter,
          paramLblFilterShortname,
          filterValue,
          prefix,
          suffix,
        };
      } else {
        title = `${config.MetricDescription} - ${config.MetricKey}`;
        comparableClass = `${isItem ? 'comps-item-field' : ''}${config.ClassCSS ?? ''}`;
        columnWidth = config.ColumnWidth;
        value = `${value ?? ''}`;
        label = `${config.MetricDescription}`;
      }

      return {
        values: [{ value, paramLblFilter, paramLblFilterShortname, filterValue, prefix, suffix }],
        title,
        comparableClass,
        columnWidth,
        label,
        IsConfidential: config.IsConfidential,
      };
    }
    return null;
  },

  getNumeralFormat(comparable, value, numeralFormat, paramLblFilterShortname, measure) {
    let filterValue = '';

    let replacedValue = numeralFormat.replace(regexConst.NUMERAL, function (match) {
      const valueAsNumber = Number(value);
      if (isNaN(valueAsNumber)) {
        return '';
      } else {
        return pixNumeral.getNumeralFormat(valueAsNumber, match);
      }
    });

    if (replacedValue !== null && replacedValue !== undefined && replacedValue !== '') {
      replacedValue = replacedValue.replace(regexConst.METRIC, function (match) {
        if (match === 'c') {
          paramLblFilterShortname = 'CurrencyParam';
          filterValue = comparable.PriceOriginalMetric || comparable.CurrencyParam || measure?.Currency;
        }
        if (match === 'a') {
          paramLblFilterShortname = 'AreaParam';
          filterValue = comparable.SurfaceOriginalMetric || comparable.AreaParam;
        }
        if (match === 'f') {
          paramLblFilterShortname = 'RentFrecuencyParam';
          filterValue = comparable.RentFreqOriginalMetric || comparable.RentFrecuencyParam || measure?.Frequency;
        }
        return '';
      });
    }

    return { replacedValue, paramLblFilterShortname, filterValue };
  },

  groupItemsInTheSameGridSlot(rawCardsConfig) {
    const cardsConfig = [];
    rawCardsConfig.forEach((cardConfig) => {
      const foundInTheSameSlotIndex = cardsConfig.findIndex((a) => {
        const sharesPlace = a.VerticalOrder === cardConfig.VerticalOrder && a.ColumnWidth === cardConfig.ColumnWidth;
        const sharesTypeInfo = a.TypeInfo === cardConfig.TypeInfo;
        // if the previous card doesn't have a typology then it's generic
        const sharesTypology = !a.isOnlyForTypology || !cardConfig.isOnlyForTypology || a.isOnlyForTypology === cardConfig.isOnlyForTypology;
        const sharesOperation = !a.isOnlyForOperation || !cardConfig.isOnlyForOperation || a.isOnlyForOperation === cardConfig.isOnlyForOperation;
        const sharesConditions = !a.Conditions || !cardConfig.Conditions || a.Conditions === cardConfig.Conditions;
        return sharesPlace && sharesTypeInfo && sharesTypology && sharesPlace && sharesOperation && sharesConditions;
      });
      if (foundInTheSameSlotIndex >= 0) {
        cardConfig.isInTheSameSlot = true;
      } else {
        cardsConfig.push(cardConfig);
      }
    });
  },

  getAttributeName(attribute) {
    return attribute.values
      .filter(({ value, filterValue }) => value || filterValue)
      .reduce((result, cv, index, filteredValues) => {
        let actualValue = getAttributeValue(cv);
        const nextValue = getAttributeValue(filteredValues[index + 1]);

        if (actualValue && cv.prefix) {
          actualValue = `${cv.prefix} ${actualValue}`;
        }

        if (actualValue && cv.suffix && (nextValue || attribute.values.length === 1)) {
          actualValue = `${actualValue}${cv.suffix} `;
        }

        return result + actualValue;
      }, '');
  },
  getAttributeValue,
};

function getAttributeValue(cv) {
  let result = '';

  if (cv?.paramLblFilter) {
    result += store.getters['paramListModule/getValueDescByParamCode'](cv.paramLblFilter, cv.value);
  } else if (cv?.paramLblFilterShortname) {
    result += `${store.getters['paramListModule/getShortNameByParamCode'](cv.paramLblFilterShortname, cv.filterValue)} ${cv.value}`;
  } else {
    result += cv?.value;
  }
  return result;
}

// TODO refactor custom events, move to other file and folder
const popUpCustmonEventFn = {
  GetBreakOptionType: (comparable) => comparableTableFeaturesUtils.getCurrentBreakOption(comparable, 'BreakOptionType'),
  GetLeaseBreakOption1Date: (comparable, { userDateFormat }) => {
    const dateValue = comparableTableFeaturesUtils.getCurrentBreakOption(comparable, 'LeaseBreakOption1Date');
    return dateValue ? moment(dateValue).format(userDateFormat) : null;
    },
    GetBaseRentAnualized1Value: (comparable, { NumeralFormat }) => {
        const valueAsANumber = comparableTableFeaturesUtils.getValueFromAnnualizedBaseRent(comparable, 'BaseRentAnualized1Value');
        return pixNumeral.getNumeralFormat(valueAsANumber, NumeralFormat);
  },
};
